import { TalerFormAttributes } from "@gnu-taler/taler-util";
import { InternationalizationAPI } from "../../context/translation.js";
import { DoubleColumnFormDesign, FormMetadata } from "../forms-types.js";
import { countryNationalityList } from "../../utils/select-ui-lists.js";
import { format, intervalToDuration, isFuture, isValid, parse } from "date-fns";
import { vqfLanguagePolicy } from "./vqf.js";

export const form_vqf_902_9_customer = (
  i18n: InternationalizationAPI,
): FormMetadata => ({
  label: i18n.fixed({
    en: "Declaration of identity of the beneficial owner (A) — customer",
    de: "Feststellung des wirtschaftlich Berechtigten (A) — Kunde",
  }),
  description: i18n.fixed({
    en: "The customer completes the declaration directly in the form.",
    de: "Der Kunde füllt die Erklärung direkt im Formular aus.",
  }),
  id: "vqf_902_9_customer",
  version: 1,
  config: VQF_902_9_customer(i18n),
});

export function VQF_902_9_customer(
  i18n: InternationalizationAPI,
): DoubleColumnFormDesign {
  const today = format(new Date(), "yyyy-MM-dd");

  return {
    type: "double-column",
    languagePolicy: vqfLanguagePolicy(i18n),
    title: i18n.fixed({
      en: "Declaration of identity of the beneficial owner",
      de: "Feststellung des wirtschaftlich Berechtigten",
    }),
    sections: [
      {
        title: i18n.fixed({
          en: "Identity of the contracting partner",
          de: "Identität des Vertragspartners",
        }),
        fields: [
          {
            id: TalerFormAttributes.IDENTITY_CONTRACTING_PARTNER,
            label: i18n.fixed({
              en: "Contracting partner",
              de: "Vertragspartner",
            }),
            type: "textArea",
            required: true,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Beneficial owner details",
          de: "Angaben zum wirtschaftlich Berechtigten",
        }),
        fields: [
          {
            id: TalerFormAttributes.IDENTITY_LIST,
            label: i18n.fixed({
              en: "Beneficial owner(s)",
              de: "Wirtschaftlich Berechtigte(r)",
            }),
            help: i18n.fixed({
              en: "The person(s) listed below is/are the beneficial owner(s) of the assets involved in the business relationship. If the contracting partner is also the sole beneficial owner of the assets, the contracting partner's detail must be set out below",
              de: "Die nachfolgend aufgeführte(n) Person(en) ist/sind an den in die Geschäftsbeziehung eingebrachten Vermögenswerten wirtschaftlich berechtigt. Ist der Vertragspartner selber allein an diesen Vermögenswerten wirtschaftlich berechtigt, so sind nachstehend seine Personalien festzuhalten",
            }),
            type: "array",
            validator(persons) {
              if (!persons || persons.length < 1) {
                return i18n.fixed({
                  en: "Can't be empty",
                  de: "Darf nicht leer sein",
                });
              }
              return undefined;
            },
            labelFieldId: TalerFormAttributes.FULL_NAME,
            fields: [
              {
                id: TalerFormAttributes.FULL_NAME,
                label: i18n.fixed({
                  en: "Full name",
                  de: "Vollständiger Name",
                }),
                type: "text",
                required: true,
              },
              {
                id: TalerFormAttributes.DOMICILE_ADDRESS,
                label: i18n.fixed({
                  en: "Domicile address",
                  de: "Sitzadresse",
                }),
                type: "textArea",
                required: true,
              },
              {
                id: TalerFormAttributes.DATE_OF_BIRTH,
                label: i18n.fixed({ en: "Date of birth", de: "Geburtsdatum" }),
                type: "isoDateText",
                placeholder: "dd/MM/yyyy",
                pattern: "dd/MM/yyyy",
                required: true,
                validator(text, form) {
                  //FIXME: why returning in this format even if pattern is in another?
                  const time = parse(text, "yyyy-MM-dd", new Date());
                  if (!isValid(time)) {
                    return i18n.fixed({
                      en: "invalid format",
                      de: "ungültiges Format",
                    });
                  }
                  if (isFuture(time)) {
                    return i18n.fixed({
                      en: "it can't be in the future",
                      de: "darf nicht in der Zukunft liegen",
                    });
                  }
                  const { years } = intervalToDuration({
                    start: time,
                    end: new Date(),
                  });
                  if (years && years > 120) {
                    return i18n.fixed({
                      en: "it can't be greater than 120 years",
                      de: "darf nicht größer als 120 Jahre sein",
                    });
                  }
                  return undefined;
                },
              },
              {
                id: TalerFormAttributes.NATIONALITY,
                label: i18n.fixed({
                  en: "Nationality",
                  de: "Staatsangehörigkeit",
                }),
                type: "selectOne",
                choices: countryNationalityList(i18n),
                preferredChoiceVals: ["CH"],
                required: true,
              },
            ],
            required: false,
          },
        ],
      },
      {
        title: i18n.fixed({ en: "Signature(s)", de: "Unterschrift(en)" }),
        description: i18n.fixed({
          en: "It is a criminal offence to deliberately provide false information on this form (article 251 of the Swiss Criminal Code, document forgery)",
          de: "Die vorsätzliche Angabe falscher Informationen in diesem Formular ist eine strafbare Handlung (Urkundenfälschung gemäss Artikel 251 des Schweizerischen Strafgesetzbuchs).",
        }),
        fields: [
          {
            type: "caption",
            label: i18n.fixed({
              en: "The contracting partner hereby undertakes to inform automatically of any changes to the information contained herein.",
              de: "Der Vertragspartner verpflichtet sich, Änderungen jeweils unaufgefordert mitzuteilen.",
            }),
          },
          {
            id: TalerFormAttributes.SIGNATURE,
            label: i18n.fixed({ en: "Signature(s)", de: "Unterschrift(en)" }),
            type: "text",
            required: true,
          },
          {
            id: TalerFormAttributes.SIGN_DATE,
            label: i18n.fixed({ en: "Date", de: "Datum" }),
            type: "isoDateText",
            placeholder: "dd/MM/yyyy",
            pattern: "dd/MM/yyyy",
            defaultValue: today,
            required: true,
            disabled: true,
          },
        ],
      },
    ],
  };
}
